import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { userAtom } from "@/atoms/userAtom";
import { locationAtom, locationPermissionAtom } from "@/atoms/recommendationAtom";
import { getAppVersion } from "@/utils/versionUtils";
import { useAtomValue } from "jotai";
import { User, LogOut, MapPin, Loader2, ExternalLink } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

interface UserMenuModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const UserMenuModal = ({ open, onOpenChange }: UserMenuModalProps) => {
  const user = useAtomValue(userAtom);
  const location = useAtomValue(locationAtom);
  const locationPermission = useAtomValue(locationPermissionAtom);
  const [isUpdatingLocation, setIsUpdatingLocation] = useState(false);

  const handleSignOut = async () => {
    try {
      const { signOut } = await import("aws-amplify/auth");
      await signOut();
      onOpenChange(false);
      window.location.reload();
    } catch (error) {
      console.error("ログアウトエラー:", error);
    }
  };

  const handleLocationUpdate = async () => {
    if (!window.requestLocationPermission) {
      toast.error("位置情報機能が利用できません");
      return;
    }

    setIsUpdatingLocation(true);
    try {
      await window.requestLocationPermission();
      toast.success("位置情報を更新しました");
    } catch (error) {
      console.error("位置情報更新エラー:", error);
      toast.error("位置情報の更新に失敗しました");
    } finally {
      setIsUpdatingLocation(false);
    }
  };

  const openGoogleMaps = () => {
    if (location.latitude && location.longitude) {
      const url = `https://maps.google.com/?q=${location.latitude},${location.longitude}`;
      window.open(url, "_blank");
    }
  };

  const hasValidLocation = location.latitude !== null && location.longitude !== null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-80 rounded-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            ユーザー情報
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {user ? (
            <>
              <div className="rounded-md bg-gray-50 p-3">
                <div className="text-sm text-gray-600">ユーザーID</div>
                <div className="break-all font-mono text-sm">{user.userId}</div>
              </div>
              <div className="rounded-md bg-gray-50 p-3">
                <div className="mb-2 flex items-center justify-between">
                  <div className="text-sm text-gray-600">現在位置</div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleLocationUpdate}
                    disabled={isUpdatingLocation}
                    className="h-7 px-2 text-xs"
                  >
                    {isUpdatingLocation ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <MapPin className="h-3 w-3" />
                    )}
                    <span className="ml-1">{isUpdatingLocation ? "更新中..." : "更新"}</span>
                  </Button>
                </div>
                {hasValidLocation ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={openGoogleMaps}
                    className="h-auto w-full justify-start p-2 text-left hover:bg-blue-50"
                  >
                    <ExternalLink className="mr-2 h-3 w-3 text-blue-600" />
                    <div className="text-xs text-blue-600">Google Mapで現在地を表示</div>
                  </Button>
                ) : (
                  <div className="p-2 text-xs text-gray-500">位置情報が取得されていません</div>
                )}
                {locationPermission.state !== "granted" && (
                  <div className="mt-1 text-xs text-amber-600">
                    位置情報へのアクセスが許可されていません
                  </div>
                )}
              </div>

              <Button
                variant="outline"
                onClick={handleSignOut}
                className="flex w-full items-center gap-2"
              >
                <LogOut className="h-4 w-4" />
                ログアウト
              </Button>

              {/* バージョン情報 */}
              <div className="text-center font-mono text-xs text-gray-500">v{getAppVersion()}</div>
            </>
          ) : (
            <div className="py-4 text-center text-gray-500">ログインしていません</div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
